import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  endOfDay,
  format,
  addWeeks,
  startOfWeek,
  endOfWeek,
  parseISO,
  startOfDay,
} from "date-fns";
import { FaRegCalendar } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useFetchDashboardTimesheetDetailsQuery } from "../../slice/logdetailSlice";
import Loader from "../common/Loader";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const dayMapping = {
  Sunday: "S",
  Monday: "M",
  Tuesday: "T",
  Wednesday: "W",
  Thursday: "T",
  Friday: "F",
  Saturday: "S",
};

const TimesheetChart = ({ onSeeMore }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const weekStartDate = format(
    startOfWeek(currentWeek, { weekStartsOn: 0 }),
    "yyyy-MM-dd"
  );

  const {
    data: timesheet_details,
    isLoading: timesheet_loading,
    refetch: timesheet_details_refetch,
  } = useFetchDashboardTimesheetDetailsQuery(`weekStart=${weekStartDate}`);
  // console.log("timesheet_details :", timesheet_details);

  // Calculate the start and end of the current week
  const startOfWeekDate = startOfWeek(currentWeek, { weekStartsOn: 0 });
  const endOfWeekDate = endOfWeek(currentWeek, { weekStartsOn: 0 });
  const navigate = useNavigate();
  const formattedStart = format(startOfWeekDate, "dd/MM/yyyy");
  const formattedEnd = format(endOfWeekDate, "dd/MM/yyyy");
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  //console.log("login_user in pproject:", login_user);
  // const { role: login_user_role } = login_user;
  const { role: login_user_role } = login_user || {};
  const handlePreviousWeek = () => {
    setCurrentWeek((prev) => addWeeks(prev, -1));
  };
  // RTK Query automatically fetches on mount; redundant refetch on mount removed

  const handleNextWeek = () => {
    setCurrentWeek((prev) => addWeeks(prev, 1));
  };
  const allDays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const chartData = allDays.map((day, index) => {
    const breakdown = timesheet_details?.dailyBreakdown?.[day] || {};

    const billableStr = (breakdown.billable_hours || "0:00 Hrs").replace(
      " Hrs",
      ""
    );
    const nonBillableStr = (breakdown.non_billable_hours || "0:00 Hrs").replace(
      " Hrs",
      ""
    );

    const [billableH, billableM] = billableStr.split(":").map(Number);
    const [nonBillableH, nonBillableM] = nonBillableStr.split(":").map(Number);

 // const billable = billableH + billableM / 60;
    // const nonBillable = nonBillableH + nonBillableM / 60;
    const billable = Number((billableH || 0) + (billableM || 0) / 60);
    const nonBillable = Number((nonBillableH || 0) + (nonBillableM || 0) / 60);


    const totalMinutes =
      (billableH + nonBillableH) * 60 + billableM + nonBillableM;
    const totalFormatted = `${Math.floor(totalMinutes / 60)}:${(
      totalMinutes % 60
    )
      .toString()
      .padStart(2, "0")}`;

      // fixed data err 
    // Compute date for this day
    const dayDate = new Date(startOfWeekDate);
    dayDate.setDate(dayDate.getDate() + index);

    return {
      day: dayMapping[day],
      billable,
      nonBillable,
      totalHours: billable + nonBillable,
      billableFormatted: `${billableStr} Hrs`,
      nonBillableFormatted: `${nonBillableStr} Hrs`,
      totalFormatted: `${totalFormatted} Hrs`,
      date: dayDate.toISOString(),
      noData: billable === 0 && nonBillable === 0 ? "No data found" : null,
    };
  });

  const handleBarClick = async (e) => {
    if (e && e.activePayload && e.activePayload[0]) {
      const clickedData = e.activePayload[0].payload;
      console.log("barchart_date :", clickedData);

      // Convert to range object (start and end of day)
      const clickedDate = parseISO(clickedData.date);
      const rangeObject = {
        startDate: startOfDay(clickedDate).toISOString(),
        endDate: endOfDay(clickedDate).toISOString(),
        key: "selection",
      };
      localStorage.setItem("timesheetProjectSpan", "today");
      localStorage.setItem("timesheetDateRange", JSON.stringify(rangeObject));
      navigate("/Timesheet");
    }
  };

  return (
    <>
      {timesheet_loading ? (
        <Loader />
      ) : (
        <div className="bg-white p-3 rounded-3">
          {/* Week Navigation */}
          <div className="row">
            <div className="col-md-4">
              <h5 className="cardsub_title text-start">
                {login_user_role === "ADMIN" || login_user_role === "CLIENT"
                  ? "All Timesheet"
                  : " My Timesheet"}
              </h5>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <button className="btn p-0" onClick={handlePreviousWeek}>
                  <IoIosArrowBack className="  text-rgba-gray" />
                </button>
                <span className="d-flex align-items-center">
                  <FaRegCalendar className="cursor-pointer me-2" />
                  {formattedStart} to {formattedEnd}
                </span>
                <button className="btn p-0" onClick={handleNextWeek}>
                  <IoIosArrowForward className="  text-rgba-gray" />
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Bar Chart */}
            <div className="col-md-8">
              <ResponsiveContainer width="100%" height={248}>
                <BarChart
                  data={chartData}
                  onClick={handleBarClick}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  style={{ cursor: "pointer" }}
                >
                  <XAxis
                    dataKey="day"
                    label={{
                      value: "Days",
                      position: "insideBottom",
                      offset: -10,
                    }}
                  />
                       {/* <YAxis
                    domain={[0, "dataMax + 2"]}
                    label={{
                      value: "Hours",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  /> */}
                  <YAxis
  domain={[0, "dataMax + 2"]}
  tickFormatter={(value) => `${Math.floor(value)}h`}
/>

                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 rounded shadow custom-tooltip">
                            <p className="mb-1">{data.day}</p>
                            {data.noData ? (
                              <p className="text-rgba-gray">No data found</p>
                            ) : (
                              <>
                                <p className="text-primary">
                                  Billable: {data.billableFormatted}
                                </p>
                                <p className="text-warning">
                                  Non-Billable: {data.nonBillableFormatted}
                                </p>
                                <p style={{ color: "green" }}>
                                  Total: {data.totalFormatted}
                                </p>
                              </>
                            )}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />

                  <Legend verticalAlign="top" align="right" />
                  <Bar
                    dataKey="billable"
                    stackId="a"
                    fill="#344BFD"
                    name="Billable"
                    barSize={20}
                    radius={
                      chartData.some((d) => d.nonBillable > 0)
                        ? [0, 0, 10, 10]
                        : [10, 10, 10, 10]
                    }
                  />
                  <Bar
                    dataKey="nonBillable"
                    stackId="a"
                    fill="#FF955A"
                    name="Non Billable"
                    barSize={20}
                    radius={[10, 10, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="col-md-4 d-flex flex-column justify-content-center">
              <div className="d-flex align-items-center mb-2">
                <div
                  style={{ width: 16, height: 16, backgroundColor: "#344BFD" }}
                  className="me-2"
                ></div>
                <span className="flex-grow-1">Billable</span>
              </div>
              <span className="text-left mb-3">
                {timesheet_details?.billableHours || "0 Hrs"}
              </span>
              <div className="d-flex align-items-center mb-2">
                <div
                  style={{ width: 16, height: 16, backgroundColor: "#ff9800" }}
                  className="me-2"
                ></div>
                <span className="flex-grow-1">Non Billable</span>
              </div>
              <span className="text-left mb-3">
                {timesheet_details?.nonBillableHours || "0 Hrs"}
              </span>
              <div className="d-flex align-items-center">
                <div
                  style={{ width: 16, height: 16, backgroundColor: "green" }}
                  className="me-2"
                ></div>
                <span className="flex-grow-1">Total Hours</span>
              </div>
              <span className="text-left mb-3">
                {timesheet_details?.totalHours || "0 Hrs"}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimesheetChart;
